import { memo, useEffect, useRef, useState } from "react";
import * as echarts from 'echarts';
import ContentCard from "./common/content-card/ContentCard";
import { ChartHeader, ChartName, ChartWrapper, ChartsWrapper, DeviceDetailsContent, DeviceModel3DWrapper, EChartsWrapper, MotorCardHeader, MotorCardWrapper, RealtimeDeviceMetricItem, RealtimeDeviceMetricItemLabel, RealtimeDeviceMetricItemValue, RealtimeDeviceMetricsUnit, RealtimeDeviceMetricsWrapper, ScreenTitleBar, TractorDetailsScreenInnerWrapper, TractorDetailsScreenWrapper } from "./TractorDetailsScreen.styled";
import SeriesColorUtils from "../../../../util/SeriesColorUtils";
import { useParams } from "react-router-dom";
import DeviceMetricApi from "../../../../api/DeviceMetricApi";
import DeviceApi from "../../../../api/DeviceApi";
import DeviceModel3D from "./component/device-model-3d/DeviceModel3D";
import EventManager from "./common/event-manager/EventManager";
import StringUtils from "../../../../util/StringUtils";
import moment from "moment";
import Navigator from "../overview/component/navigator/Navigator";

interface TractorDetailsScreenProps {

}

let TractorDetailsScreen: React.FC<TractorDetailsScreenProps> = (props) => {
    // params
    let { deviceId } = useParams();

    // api
    let deviceApi = new DeviceApi();
    let deviceMetricApi = new DeviceMetricApi();

    // state
    let [deviceView, setDeviceView] = useState<any>(null);
    let [latestDeviceMetrics, setLatestDeviceMetrics] = useState<any>(null);
    
    // event manager
    let eventManagerRef = useRef(new EventManager());
    let motorViewsRef = useRef([]);

    // function
    let fetchDeviceView = async () => {
        if (deviceId == null) {
            return;
        }
        let deviceView = (await deviceApi.getDeviceViewByDeviceId(deviceId)).data.data;
        setDeviceView(deviceView);

        // motor views
        if (motorViewsRef.current.length <=0) {
            let motorViews: any[] = [];
            for (let motorTypeInfo of [{type: 'moving', name: '走行电机'},{type: 'steering', name: '转向电机'}]) {
                for (let motorIndex = 1; motorIndex <= 4; motorIndex++) {
                    let motorView = {} as any;
    
                    if (deviceView) {
                        let motorProperties = [];
                        motorView.properties = motorProperties;
                        let deviceTemplateConfiguration = JSON.parse(deviceView.deviceTemplate.configuration);
                        for (let property of deviceTemplateConfiguration.properties) {
                            if (property.key.startsWith(`${motorTypeInfo.type}Motor${motorIndex}`)) {
                                motorProperties.push(property);
                            }
                        }
                    }
                    motorView.name = `${motorTypeInfo.name}${motorIndex}`;
                    motorView.key = `${motorTypeInfo.type}Motor${motorIndex}`;
                    motorViews.push(motorView);
                }
            }
            motorViewsRef.current = motorViews;
        }
    };

    let fetchLatestDeviceMetrics = async () => {
        if (deviceId == null) {
            return;
        }
        let latestDeviceMetrics = (await deviceMetricApi.getRealtimeDeviceMetricsSeries(deviceId)).data.data;
        setLatestDeviceMetrics(latestDeviceMetrics);
        eventManagerRef.current.notifyRealtimeDeviceMetricsSeriesUpdated(latestDeviceMetrics);
    };

    // effects
    useEffect(()=> {
        fetchDeviceView();
    }, [deviceId]);

    useEffect(()=> {
        fetchLatestDeviceMetrics();
        let refreshTask = window.setInterval(fetchLatestDeviceMetrics, 10 * 1000);
        return ()=> window.clearInterval(refreshTask);
    }, [deviceId]);

    // render
    return <TractorDetailsScreenWrapper>
        <TractorDetailsScreenInnerWrapper>
            <ScreenTitleBar>
                <Navigator selected="DEVICE_DETAILS"/>
            </ScreenTitleBar>
            <DeviceDetailsContent>
                <DeviceModel3DWrapper>
                    <DeviceModel3D eventManager={eventManagerRef.current} />
                </DeviceModel3DWrapper>
                {motorViewsRef.current.map((motorView, index) => {
                    return <MotorCard key={motorView.key} deviceView={deviceView} motorView={motorView} eventManager={eventManagerRef.current} />
                })}
            </DeviceDetailsContent>            
        </TractorDetailsScreenInnerWrapper>
    </TractorDetailsScreenWrapper>
};

interface MotorCardProps {
    deviceView: any,
    motorView: any,
    eventManager: EventManager;
}

let MotorCard: React.FC<MotorCardProps> = (props) => {
    let deviceView = props.deviceView;
    let motorView = props.motorView;

    // state
    let [motorLatestDeviceMetrics, setMotoLatestDeviceMetrics] = useState<any>({});

    // ref
    let echartsDomsRef = useRef<any>({});
    let echartsRef = useRef<any>({});
    let latestDeviceMetricsUpdatedListenersRef = useRef({});
    let motorLatestDeviceMetricsRef = useRef({});

    // init ECharts
    let initECharts = (dom: HTMLElement, key: string)=> {
        if (dom == null) {
            // remove listeners
            latestDeviceMetricsUpdatedListenersRef.current[key] = null;

            // element unmount event
            let echarts = echartsRef.current[key];
            echarts?.dispose();
            echartsRef.current[key] = null;
            return;
        }
        if (echarts.getInstanceByDom(dom)) {
            return;
        }

        // save to reference
        echartsDomsRef.current[key]=dom;

        // init echarts
        let chart = echarts.init(dom);
        echartsRef.current[key]=chart;

        let properties = motorView.properties.filter(property => {
            return property.key.toLowerCase().endsWith(key.toLowerCase());
        });
        let property = properties[0];

        // init options
        let formatTimestampAxis = (value)=> {
            let text = moment(value).format('HH:mm:ss');
            text=text.replace(' ', '\n');
            return text;
        };
        
        let option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    animation: false
                },
                confine: true,
                backgroundColor: "rgba(0,0,0,0.7)",
                formatter: function(params) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value==null) {
                            continue;
                        }

                        let errorText = '';
                        if (property.min != null && Number(param.value[1]) < property.min) {
                            errorText = '（故障）';
                        } else if (property.max != null && Number(param.value[1]) > property.max) {
                            errorText = '（故障）';
                        }
                        
                        seriesContent += `
                            <div style="padding: 5px 0px;">
                                <span style="display: inline-block;">${param.seriesName}:</span> 
                                <span style="display: inline-block">${param.value[1]} ${errorText}</span>
                            </div>
                        `;
                        break;
                    }
                    seriesContent += `
                        <div>
                            <span style="display: inline-block;">时间:</span> 
                            <span style="display: inline-block">${moment(params[0].value[0]).format('HH:mm:ss')}</span>
                        </div>
                    `;
                    let content=`
                        <div style="font-size: 9px; color: #EEEEEE;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                x: 20,
                y: 10,
                x2: 5,
                y2: 20,
            },
            xAxis: {
                type: 'time',
                axisLine: {
                    show: true
                },
                axisTick: {
                    show: true,
                },
                minorTick: {
                    show: true,
                },
                minorSplitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    },
                },
                splitNumber: 2,
                axisLabel: {
                    show: true,
                    fontSize: 7,
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    }
                },
            },
            yAxis: {
                type: 'value',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                axisTick: {
                    show: true,
                    inside: true,
                },
                axisLabel: {
                    show: true,
                    fontSize: 7,
                    distance: 0,
                    margin: 2,
                },
                minorTick: {
                    show: false
                },
                minorSplitLine: {
                    show: true,
                    fontSize: 7,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    }
                },
                splitNumber: 2,
                min: property.min != null ? parseFloat(property.min) : 'dataMin',
                max: property.max != null ? parseFloat(property.max) : 'dataMax',
            },
            dataZoom: [
                {
                  type: 'inside',
                  start: 90,
                  end: 100
                }
            ],
            series: properties.map((property, propertyIndex)=> {
                return {
                    name: property.name,
                    type: 'line',
                    color: SeriesColorUtils.getSeriesColor(propertyIndex),
                    smooth: false,
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.2,
                    },
                    data: [],
                    shadowBlur: 20,
                }
            })
        };

        chart.setOption(option);
        chart.group='DeviceRealtimeMetrics';
        echarts.connect('DeviceRealtimeMetrics');

        // metrics updated listener
        latestDeviceMetricsUpdatedListenersRef.current[key] = (deviceMetricsSeriesList: any)=> {
            let seriesDatas = [];
            for (let property of properties) {
                let deviceMetricsSeries = deviceMetricsSeriesList.find(deviceMetricsSeries => deviceMetricsSeries.property.key == property.key);
                if (deviceMetricsSeries == null) {
                    continue;
                }
                
                // series data
                let seriesData = [];
                let metrics = deviceMetricsSeries.metrics;
                let lastMetricTimestamp: number = null;
                let paddingGapMillis = 5 * 10000;
                for (let i = 0; i < metrics.length; i++) {
                    let metric = metrics[i];
                    
                    // filter error metrics (mainly caused by sensor errors)
                    let errorMetric = false;
                    if (property.min != null && Number(metric.value) < property.min) {
                        errorMetric = true;
                    } else if (property.max != null && Number(metric.value) > property.max) {
                        errorMetric = true;
                    }
                    if (errorMetric) {
                        if (seriesData[seriesData.length-1] != null) {
                            seriesData.push(null);                        }
                        continue;
                    }
                    
                    // pad data point
                    if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                        // down data point
                        // let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), null]};
                        // seriesData.push(downDataPoint);

                        // up data point
                        // let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), null]};
                        // seriesData.push(upDataPoint);
                        seriesData.push(null);
                    }
                    lastMetricTimestamp = metric.timestamp;

                    // add to series data
                    seriesData.push({
                        name: metric.timestamp + '',
                        value: [Number(metric.timestamp), Number(metric.value)],
                    });
                }

                // seriesDatas
                seriesDatas.push(seriesData);
            }

            // min/max timestam
            let minTime: number;
            let maxTime: number;
            for(let i=0; i<seriesDatas.length; i++) {
                let seriesData = seriesDatas[i];
                if (seriesData.length <= 0) {
                    continue;
                }
                let minTimestamp = seriesData[0].value[0];
                let maxTimestamp = seriesData[seriesData.length-1].value[0];
                minTime = minTime == null ? minTimestamp : Math.min(minTime, minTimestamp);
                maxTime = maxTime == null ? maxTimestamp : Math.max(maxTime, maxTimestamp);
            }
            echartsRef.current[key].setOption({
                xAxis: {
                    min: minTime,
                    max: maxTime,
                },
                series: seriesDatas.map((seriesData, seriesIndex)=> {
                    return {
                        data: seriesData
                    }
                }),
            });
        };
    }

    // latest device metrics updated
    let latestDeviceMetricsUpdatedListener = (deviceMetricsSeriesList: any[])=> {
        let keys = [
            'commandSpeed',
            'acceleration',
            'deceleration',
            'acceleration',
            'temp',
            'driverTemp',
            'soc',
            'control',
        ];

        let motorLatestDeviceMetrics = motorLatestDeviceMetricsRef.current;
        for (let key of keys) {
            let deviceMetricsSeries = deviceMetricsSeriesList.find(deviceMetricsSeries => deviceMetricsSeries.property.key.toLowerCase().endsWith(key.toLowerCase()));
            if (deviceMetricsSeries) {
                motorLatestDeviceMetrics[key] = deviceMetricsSeries.metrics[deviceMetricsSeries.metrics.length - 1];
            }
        }
        setMotoLatestDeviceMetrics({...motorLatestDeviceMetricsRef.current});
    };


    // init ECharts reference
    let initEChartsRef = useRef(initECharts);

    // effects
    useEffect(()=> {
        props.eventManager.addRealtimeDeviceMetricsSeriesUpdatedListener((deviceMetricsSeriesList)=> {
            latestDeviceMetricsUpdatedListener(deviceMetricsSeriesList);
            for (let key in latestDeviceMetricsUpdatedListenersRef.current) {
                latestDeviceMetricsUpdatedListenersRef.current[key](deviceMetricsSeriesList);
            }
        });
    }, []);

    // render
    return <MotorCardWrapper>
        <MotorCardHeader>{motorView.name}</MotorCardHeader>
        <ChartsWrapper>
            <ChartWrapper>
                <ChartHeader>
                    <ChartName>转速 / RPMS</ChartName>
                </ChartHeader>
                <EChartsDom propertyKey="speed" refCallback={initEChartsRef.current} />
            </ChartWrapper>
            <ChartWrapper>
                <ChartHeader>
                    <ChartName>温度 / ℃</ChartName>
                </ChartHeader>
                <EChartsDom propertyKey="temp" refCallback={initEChartsRef.current} />
            </ChartWrapper>
        </ChartsWrapper>

        <RealtimeDeviceMetricsWrapper>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>转速</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.speed?.value)}
                    <RealtimeDeviceMetricsUnit>RPMS</RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>命令转速</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.commandSpeed?.value)}
                    <RealtimeDeviceMetricsUnit>RPMS</RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>加速度</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.acceleration?.value)}
                    <RealtimeDeviceMetricsUnit></RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>减速度</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.deceleration?.value)}
                    <RealtimeDeviceMetricsUnit></RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>电流</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.acceleration?.value)}
                    <RealtimeDeviceMetricsUnit>ARMS</RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>电机温度</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.temp?.value)}
                    <RealtimeDeviceMetricsUnit>℃</RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>驱动器温度</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.driverTemp?.value)}
                    <RealtimeDeviceMetricsUnit>℃</RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            {motorView.type == 'moving' ? <RealtimeDeviceMetricItem>
                <RealtimeDeviceMetricItemLabel>电量</RealtimeDeviceMetricItemLabel>
                <RealtimeDeviceMetricItemValue>
                    {StringUtils.emptyToSlash(motorLatestDeviceMetrics.soc?.value)}
                    <RealtimeDeviceMetricsUnit>%</RealtimeDeviceMetricsUnit>
                </RealtimeDeviceMetricItemValue>
            </RealtimeDeviceMetricItem>
            : <RealtimeDeviceMetricItem>
            <RealtimeDeviceMetricItemLabel>控制字</RealtimeDeviceMetricItemLabel>
            <RealtimeDeviceMetricItemValue>
                {motorLatestDeviceMetrics.control ? '0x' + parseInt(motorLatestDeviceMetrics.control.value).toString(16) : 'N/A'}
                <RealtimeDeviceMetricsUnit>%</RealtimeDeviceMetricsUnit>
            </RealtimeDeviceMetricItemValue>
        </RealtimeDeviceMetricItem>
            }
        </RealtimeDeviceMetricsWrapper>
    </MotorCardWrapper>;
}


interface EChartsDomProps {
    propertyKey: string;
    refCallback: (dom: HTMLDivElement, key: string)=> void;
}

let EChartsDom: React.FC<EChartsDomProps> = memo((props)=> {
    return <EChartsWrapper ref={(dom)=> props.refCallback(dom, props.propertyKey)}>
    </EChartsWrapper>
});


export default TractorDetailsScreen;


